import { createContext, useContext, useState, ReactNode, useEffect } from "react"

interface RisksContextType {
  openFamilies: string[]
  setOpenFamilies: (families: string[]) => void
  toggleFamily: (id: string) => void
  saveScrollPosition: () => void
  restoreScrollPosition: () => void
}

const RisksContext = createContext<RisksContextType | undefined>(undefined)


const SCROLL_STORAGE_KEY = "risks-validated-scroll-position"
const OPEN_FAMILIES_KEY = "risks-validated-open-families"

export function RisksProvider({ children }: { children: ReactNode }) {
  const [openFamilies, setOpenFamilies] = useState<string[]>(() => {
    const saved = localStorage.getItem(OPEN_FAMILIES_KEY)
    return saved ? JSON.parse(saved) : []
  })

  function saveScrollPosition() {
    const element = document.querySelector(".page-navigation-scrollable") as HTMLElement
    if (element) {
      localStorage.setItem(SCROLL_STORAGE_KEY, String(element.scrollTop))
    }
  }

  function restoreScrollPosition() {
    setTimeout(() => {
      const element = document.querySelector(".page-navigation-scrollable") as HTMLElement
      if (element) {
        const savedPosition = localStorage.getItem(SCROLL_STORAGE_KEY)
        if (savedPosition) {
          element.scrollTop = parseInt(savedPosition, 10)
          localStorage.removeItem(SCROLL_STORAGE_KEY)
        }
      }
    }, 100)
  }

  // Persist open families to localStorage
  useEffect(() => {
    localStorage.setItem(OPEN_FAMILIES_KEY, JSON.stringify(openFamilies))
  }, [openFamilies])

  function toggleFamily(id: string) {
    setOpenFamilies((prev) => {
      const newState = prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat([id])
      // Scroll to family when opening
      if (newState.includes(id)) {
        setTimeout(() => {
          document.getElementById(`family-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
        }, 0)
      }
      return newState
    })
  }

  return (
    <RisksContext.Provider value={{ openFamilies, setOpenFamilies, toggleFamily, saveScrollPosition, restoreScrollPosition }}>
      {children}
    </RisksContext.Provider>
  )
}

export function useRisksContext() {
  const context = useContext(RisksContext)
  if (context === undefined) {
    throw new Error("useRisksContext must be used within RisksProvider")
  }
  return context
}
